/**
 * /legal/paia — Promotion of Access to Information Act manual.
 *
 * B6 (white-label public POPIA): on a tenant subdomain ({slug}.elreign.io)
 * the Information Officer (the PAIA officer) block — and the request-
 * submission email it is addressed to — show the TENANT's designated
 * IO (name + the alias/role email surfaced by the public officers endpoint).
 * On apex elreign.io / app*.elreign.io / an unknown slug / a tenant with no
 * designated IO, it falls back to Elreign's platform Information Officer
 * (Paul Prinsloo / info@elreign.io) — unchanged from before.
 */

import { createFileRoute } from '@tanstack/react-router';
import { LegalLayout } from '../../components/layouts/legal-layout';
import { resolveTenantSlugFromHost } from '../../lib/tenant';
import { useTenantPopiaOfficersBySlug } from '../../lib/api';
import { displayPersonName } from '../../lib/format/person-name';

export const Route = createFileRoute('/legal/paia')({
  component: PaiaPage,
});

// ── Elreign platform default Information Officer (PAIA) ───────────────────────
// Rendered on apex / app / unknown-slug / no-designated-officer. Unchanged from
// the pre-B6 hard-coded block.
const ELREIGN_DEFAULT_IO = {
  name: 'Paul Prinsloo',
  email: 'info@elreign.io',
} as const;

const TOC = [
  { anchor: 'overview',       label: 'Overview' },
  { anchor: 'how-to-request', label: 'How to request information' },
  { anchor: 'fees',           label: 'Fees' },
  { anchor: 'exemptions',     label: 'Exemptions' },
  { anchor: 'contact',        label: 'Information Officer' },
];

function PaiaPage() {
  // B6: resolve the tenant slug from the host (same resolver branding uses).
  // null on apex / app / local-dev → the hook is disabled and we render the
  // Elreign default IO. The PAIA officer is the Information Officer.
  const slug = resolveTenantSlugFromHost(
    typeof window !== 'undefined' ? window.location.host : '',
    typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : undefined,
  );
  const officersQ = useTenantPopiaOfficersBySlug(slug);
  const tenantIo = officersQ.data?.officers.io ?? null;

  // Render the tenant's designated IO when on a tenant subdomain AND an
  // officer is designated; otherwise the Elreign platform default. The
  // request-submission email is addressed to the SAME officer so the manual
  // stays internally consistent.
  const ioName = displayPersonName(tenantIo?.displayName) || ELREIGN_DEFAULT_IO.name;
  const ioEmail = tenantIo?.email ?? ELREIGN_DEFAULT_IO.email;

  return (
    <LegalLayout heading="PAIA information manual." lastUpdated="7 May 2026" toc={TOC}>
      <h2 id="overview">Overview</h2>
      <p>
        The Promotion of Access to Information Act 2 of 2000 (<strong>PAIA</strong>) gives every
        person the right to access information held by a private body where that information is
        required for the exercise or protection of a right.
      </p>
      <p>
        This manual is published in terms of section 51 of PAIA. It describes the records held by
        Elreign Legacy (Pty) Ltd, the procedure for requesting access to those records, and the fees
        applicable.
      </p>
      <p>
        Elreign is also a data operator for personal information held on behalf of fiduciary firms
        (responsible parties). Requests for information held on behalf of a responsible party should,
        in the first instance, be directed to that firm.
      </p>

      <h2 id="how-to-request">How to request information</h2>
      <p>
        A request must be submitted on the prescribed Form C (available from the South African
        Department of Justice website). The completed form must be addressed to our
        Information Officer and submitted by:
      </p>
      <ul>
        <li>
          Email:{' '}
          <a href={`mailto:${ioEmail}`} style={{ color: 'var(--primary-600)' }}>
            {ioEmail}
          </a>
        </li>
        <li>Post: as noted under Information Officer below</li>
      </ul>
      <p>
        We will acknowledge receipt within 3 business days. A decision will be made within 30 days
        unless an extension is granted under section 57 of PAIA.
      </p>
      <p>
        If you cannot read or write, or have a disability that prevents you from submitting a
        written request, you may make an oral request. The person receiving the request will reduce
        it to writing on your behalf.
      </p>

      <h2 id="fees">Fees</h2>
      <p>
        A request fee of R 35 is payable on submission (as prescribed by Government Gazette).
        Additional access fees apply depending on the form in which information is provided.
        Current prescribed fees are published in Government Notice R 225 of 2022.
      </p>
      <p>
        Fees may be waived for requesters who establish that they cannot afford them. Applications
        for waiver should accompany the request.
      </p>
      <p>
        No fee is payable for a request for personal information about yourself.
      </p>

      <h2 id="exemptions">Exemptions</h2>
      <p>
        Access to certain categories of record may be refused under PAIA. Grounds for refusal
        include, but are not limited to:
      </p>
      <ul>
        <li>Mandatory protection of privacy of a third party (s 63)</li>
        <li>Mandatory protection of commercial information (s 64)</li>
        <li>Mandatory protection of confidential information of a third party (s 65)</li>
        <li>Safety of an individual or public safety (s 66)</li>
        <li>Information protected under other legislation (s 67)</li>
      </ul>
      <p>
        Where access is refused, we will notify you in writing of the grounds for refusal and
        advise you of your right to lodge an internal appeal or approach a court for relief.
      </p>

      <h2 id="contact">Information Officer</h2>
      <div className="legal-info-box">
        <div className="eyebrow">Contact</div>
        <p style={{ margin: 0 }}>
          Information Officer (PAIA): {ioName}<br />
          Email:{' '}
          <a href={`mailto:${ioEmail}`} style={{ color: 'var(--primary-600)' }}>
            {ioEmail}
          </a>
          <br />
          Postal address: Cape Town, South Africa (full address TBD on registration)
        </p>
      </div>
      <p style={{ fontSize: 15 }}>
        The prescribed Form C and current fee schedule are available from the{' '}
        <a
          href="https://www.justice.gov.za/inforeg"
          style={{ color: 'var(--primary-600)' }}
          rel="noopener noreferrer"
          target="_blank"
        >
          South African Information Regulator
        </a>
        .
      </p>
    </LegalLayout>
  );
}
